import React, { useEffect, useState } from 'react';
import { ArrowLeft, Trash2, Save } from 'lucide-react';
import { useDispatch } from 'react-redux';
import {
  success,
  error as toastError,
  WarningDialog,
} from '@innovyze/stylovyze';
import {
  getSkill,
  createSkill,
  updateSkill,
  deleteSkill,
} from '@/services/skillsApi';
import type { TenantSkill, UserSkill } from '@Types/skills';
import { SkillsAsyncState } from './SkillsAsyncState';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

type EditableSkill = UserSkill | TenantSkill;

interface SkillEditorProps {
  skillId?: string;
  createScope?: 'user' | 'tenant';
  readOnly?: boolean;
  onBack: () => void;
  onSaved: (skill: EditableSkill) => void;
  onDeleted?: () => void;
}

export const SkillEditor = ({
  skillId,
  createScope = 'user',
  readOnly = false,
  onBack,
  onSaved,
  onDeleted,
}: SkillEditorProps) => {
  const dispatch = useDispatch();
  const isCreate = !skillId;

  const [skill, setSkill] = useState<EditableSkill | null>(null);
  const scope: 'user' | 'tenant' = isCreate
    ? createScope
    : skill?.scope ?? createScope;
  const isTenant = scope === 'tenant';
  const [loading, setLoading] = useState(!isCreate);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!skillId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getSkill(skillId)
      .then(s => {
        if (cancelled) return;
        setSkill(s);
        setName(s.name);
        setDescription(s.description);
        setBody(s.body || '');
      })
      .catch(e => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to load skill');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [skillId]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const trimmedBody = body.trim();

  const dirty = isCreate
    ? trimmedName !== '' || trimmedDescription !== '' || trimmedBody !== ''
    : !!skill &&
      (name !== skill.name ||
        description !== skill.description ||
        body !== (skill.body || ''));

  const canSave =
    trimmedName !== '' && trimmedDescription !== '' && trimmedBody !== '';

  const handleSave = async () => {
    if (!canSave || saving) return;
    if (!isCreate && !dirty) return;
    const existing = skill;
    if (!isCreate && !existing) return;
    setSaving(true);
    try {
      const saved =
        isCreate || !existing
          ? await createSkill({
              name: trimmedName,
              description: trimmedDescription,
              body,
              scope,
            })
          : await updateSkill(existing.skill_id, {
              name: trimmedName,
              description: trimmedDescription,
              body,
            });
      trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.SKILLS_SAVE, {
        skill_id: saved.skill_id,
        scope,
        action: isCreate ? 'create' : 'update',
      });
      dispatch(
        success(
          isCreate
            ? isTenant
              ? 'Tenant skill created'
              : 'Skill created'
            : 'Skill saved',
        ),
      );
      onSaved(saved);
      onBack();
    } catch (e) {
      trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.SKILLS_SAVE_ERROR, {
        scope,
        action: isCreate ? 'create' : 'update',
      });
      dispatch(toastError(e instanceof Error ? e.message : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!skill) return;
    setDeleting(true);
    try {
      await deleteSkill(skill.skill_id);
      trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.SKILLS_DELETE, {
        skill_id: skill.skill_id,
        scope: skill.scope,
      });
      dispatch(success(`Deleted "${skill.name}"`));
      onDeleted?.();
    } catch (e) {
      trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.SKILLS_DELETE_ERROR, {
        skill_id: skill.skill_id,
        scope: skill.scope,
      });
      dispatch(toastError(e instanceof Error ? e.message : 'Delete failed'));
      setDeleting(false);
    }
  };

  const editorError = loadError || (!isCreate && !skill ? 'Not found' : null);

  return (
    <SkillsAsyncState
      loading={loading}
      error={editorError}
      onRetry={onBack}
      retryLabel="Back">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => {
              trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.SKILLS_EDITOR_BACK, {
                scope,
                action: isCreate ? 'create' : 'edit',
              });
              onBack();
            }}
            className="inline-flex items-center gap-1 text-sm text-[#252F33] hover:text-brand-link">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-2 text-xs text-[#768D95]">
            {isTenant && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                Tenant skill
              </span>
            )}
            <span>
              {isCreate
                ? isTenant
                  ? 'New tenant skill — visible to everyone in your tenant'
                  : 'New skill'
                : readOnly
                ? `Read-only · v${skill?.version ?? ''}`
                : `Authored by you · v${skill?.version ?? ''}`}
            </span>
          </div>
        </div>

        <div className="space-y-4 flex-1 overflow-y-auto pr-1">
          <div>
            <label
              htmlFor="skill-editor-name"
              className="block text-xs font-semibold text-[#252F33] mb-1">
              Name
            </label>
            <input
              id="skill-editor-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              readOnly={readOnly}
              disabled={readOnly}
              maxLength={100}
              placeholder={isCreate ? 'A short, unique name' : undefined}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-dark disabled:bg-slate-50 disabled:text-[#252F33]"
            />
          </div>

          <div>
            <label
              htmlFor="skill-editor-description"
              className="block text-xs font-semibold text-[#252F33] mb-1">
              Description
            </label>
            <p className="text-xs text-[#768D95] mb-1">
              A short overview of what this skill does. Shown on the skill card
              and in tooltips.
            </p>
            <textarea
              id="skill-editor-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              readOnly={readOnly}
              disabled={readOnly}
              maxLength={1000}
              rows={3}
              placeholder={
                isCreate
                  ? 'e.g. User wants to ... Does NOT apply to ...'
                  : undefined
              }
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-dark resize-y disabled:bg-slate-50 disabled:text-[#252F33]"
            />
          </div>

          <div>
            <label
              htmlFor="skill-editor-body"
              className="block text-xs font-semibold text-[#252F33] mb-1">
              Body
            </label>
            <p className="text-xs text-[#768D95] mb-1">
              Injected into the system prompt when the skill is active. Markdown
              supported.
            </p>
            <textarea
              id="skill-editor-body"
              value={body}
              onChange={e => setBody(e.target.value)}
              readOnly={readOnly}
              disabled={readOnly}
              maxLength={8192}
              rows={12}
              placeholder={
                isCreate ? 'Instructions, context, or examples ...' : undefined
              }
              className="w-full px-3 py-2 text-sm font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-dark resize-y disabled:bg-slate-50 disabled:text-[#252F33]"
            />
            {!readOnly && (
              <p className="text-xs text-[#768D95] mt-1">
                {body.length} / 8192
              </p>
            )}
          </div>
        </div>

        {!readOnly && (
          <div className="flex-shrink-0 flex items-center justify-between pt-4 mt-4 border-t border-slate-200/80">
            {!isCreate && skill ? (
              <button
                type="button"
                disabled={deleting || saving}
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1 text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md disabled:opacity-50">
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!canSave || (!isCreate && !dirty) || saving}
                onClick={handleSave}
                className="inline-flex items-center gap-1 text-sm font-medium px-4 py-1.5 rounded-md bg-brand-link text-white hover:bg-[#005a94] disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving
                  ? isCreate
                    ? 'Creating…'
                    : 'Saving…'
                  : isCreate
                  ? 'Create skill'
                  : 'Save changes'}
              </button>
            </div>
          </div>
        )}

        {!isCreate && skill && (
          <WarningDialog
            open={confirmDelete}
            title="Delete skill?"
            cancelText="Cancel"
            confirmText="Delete"
            onCancel={() => setConfirmDelete(false)}
            onConfirm={handleDelete}>
            <p className="text-sm text-[#252F33]">
              {`"${skill.name}" will be deleted. This cannot be undone.`}
            </p>
          </WarningDialog>
        )}
      </div>
    </SkillsAsyncState>
  );
};
